export interface BrowserInfo {
  name: string;
  version: string;
  majorVersion: number;
}

export interface OSInfo {
  name: string;
  version: string;
  majorVersion: number;
}

/**
 * 浏览器最低版本要求。
 *
 * 门槛由 Tailwind CSS v4 决定：它依赖 @property、color-mix()、oklch()、级联层等
 * CSS 特性，这些**无法 polyfill**，低于门槛页面会直接崩（颜色/间距失效）。
 * Vue 3.5（原生 ES2015）与 Vite 8（build target: baseline-widely-available）
 * 的要求都低于它，不是瓶颈。
 */
export const BROWSER_VERSION_THRESHOLD: Record<string, string> = {
  Chrome: "111",
  "Edge Chromium": "111",
  Firefox: "128",
  Safari: "16.4",
  Opera: "97", // Opera 97 起基于 Chromium 111
};

/** 无论版本一律不支持的浏览器 */
export const UNSUPPORTED_BROWSERS: readonly string[] = ["IE", "Legacy Edge"];

/**
 * 系统版本阈值：只保留 iOS。
 *
 * Windows / macOS / Android 的版本号并不能代表浏览器的 CSS 能力；
 * 而 iOS 上所有浏览器都必须用 WebKit，所以 iOS 版本等价于 Safari 版本。
 */
export const OS_VERSION_THRESHOLD: Record<string, string> = {
  iOS: "16.4",
};

/** 逐段比较版本号：a > b 返回正数，a < b 返回负数，相等返回 0 */
export function compareVersion(a: string, b: string): number {
  const pa = a.split(".").map((n) => Number(n) || 0);
  const pb = b.split(".").map((n) => Number(n) || 0);
  const len = Math.max(pa.length, pb.length);

  for (let i = 0; i < len; i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

/**
 * 取版本串的主版本号（第一个点分段）："111.0.5563.110" → 111，"16.4" → 16。
 *
 * 不能直接用 Number()：它对 "10.15.7" 这类多段版本号会得到 NaN。
 */
function parseMajor(version: string): number {
  const [major = ""] = version.split(".", 1);
  return Number(major) || 0;
}

/**
 * 现代 CSS 能力检测。
 *
 * 这几项的浏览器支持边界（Chrome 111 / Safari 16.4 / Firefox 128）
 * 恰好与 Tailwind v4 的要求重合，比解析 UA 准确得多：
 * 360、QQ、夸克、Arc 这类套壳浏览器会伪装 Chrome 版本号，UA 判断必漏。
 */
export function supportsModernCss(): { ok: boolean; missing: string[] } {
  if (typeof CSS === "undefined" || typeof CSS.supports !== "function") {
    return { ok: false, missing: ["CSS.supports"] };
  }

  const missing: string[] = [];
  const checks: [label: string, condition: () => boolean][] = [
    ["oklch 颜色", () => CSS.supports("color", "oklch(0.7 0.19 47.6)")],
    ["color-mix()", () => CSS.supports("color", "color-mix(in oklab, red, blue)")],
    [":where() 选择器", () => CSS.supports("selector(:where(div))")],
  ];

  for (const [label, condition] of checks) {
    try {
      if (!condition()) missing.push(label);
    } catch {
      missing.push(label);
    }
  }

  return { ok: missing.length === 0, missing };
}

const DEFAULT_MESSAGE = "您的浏览器或操作系统版本过低，可能会影响体验，建议升级到最新版本。";

export function parseBrowser(): BrowserInfo | null {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent;
  let name: string;
  let version: string;
  let majorVersion: number;

  const ieMatch = ua.match(/(?:MSIE |Trident.*rv:)([\d.]+)/);
  if (ieMatch) {
    name = "IE";
    version = ieMatch[1]!;
    majorVersion = parseMajor(version);
    return { name, version, majorVersion };
  }

  const oldEdgeMatch = ua.match(/Edge\/([\d.]+)/);
  if (oldEdgeMatch) {
    name = "Legacy Edge";
    version = oldEdgeMatch[1]!;
    majorVersion = parseMajor(version);
    return { name, version, majorVersion };
  }

  const edgMatch = ua.match(/Edg\/([\d.]+)/);
  if (edgMatch) {
    name = "Edge Chromium";
    version = edgMatch[1]!;
    majorVersion = parseMajor(version);
    return { name, version, majorVersion };
  }

  const chromeMatch = ua.match(/Chrome\/([\d.]+)/);
  if (chromeMatch && !ua.includes("Edg") && !ua.includes("OPR")) {
    name = "Chrome";
    version = chromeMatch[1]!;
    majorVersion = parseMajor(version);
    return { name, version, majorVersion };
  }

  const firefoxMatch = ua.match(/Firefox\/([\d.]+)/);
  if (firefoxMatch) {
    name = "Firefox";
    version = firefoxMatch[1]!;
    majorVersion = parseMajor(version);
    return { name, version, majorVersion };
  }

  const safariMatch = ua.match(/Version\/([\d.]+).*Safari/);
  if (safariMatch && !ua.includes("Chrome") && !ua.includes("Edg")) {
    name = "Safari";
    version = safariMatch[1]!;
    majorVersion = parseMajor(version);
    return { name, version, majorVersion };
  }

  const operaMatch = ua.match(/OPR\/([\d.]+)/);
  if (operaMatch) {
    name = "Opera";
    version = operaMatch[1]!;
    majorVersion = parseMajor(version);
    return { name, version, majorVersion };
  }

  return null;
}

export function parseOS(): OSInfo | null {
  if (typeof navigator === "undefined") return null;
  const ua = navigator.userAgent;
  let name: string;
  let version: string;
  let majorVersion: number;

  const winMatch = ua.match(/Windows NT ([\d.]+)/);
  if (winMatch) {
    name = "Windows";
    version = winMatch[1]!;
    majorVersion = parseMajor(version);
    return { name, version, majorVersion };
  }

  const macMatch = ua.match(/Mac OS X ([\d_]+)/);
  if (macMatch) {
    name = "macOS";
    version = macMatch[1]!.replaceAll("_", ".");
    majorVersion = parseMajor(version);
    return { name, version, majorVersion };
  }

  const iosMatch = ua.match(/iPhone OS ([\d_]+)/) || ua.match(/iPad; CPU OS ([\d_]+)/);
  if (iosMatch) {
    name = "iOS";
    version = iosMatch[1]!.replaceAll("_", ".");
    majorVersion = parseMajor(version);
    return { name, version, majorVersion };
  }

  const androidMatch = ua.match(/Android ([\d.]+)/);
  if (androidMatch) {
    name = "Android";
    version = androidMatch[1]!;
    majorVersion = parseMajor(version);
    return { name, version, majorVersion };
  }

  return null;
}

export function isLowVersion(
  browser: BrowserInfo | null,
  os: OSInfo | null,
): { isLow: boolean; message: string } {
  let isLowBrowser = false;
  let isLowOS = false;
  let browserMsg = "";
  let osMsg = "";

  if (browser) {
    const threshold = BROWSER_VERSION_THRESHOLD[browser.name];
    if (UNSUPPORTED_BROWSERS.includes(browser.name)) {
      isLowBrowser = true;
      browserMsg = `${browser.name} ${browser.version}`;
    } else if (threshold && compareVersion(browser.version, threshold) < 0) {
      isLowBrowser = true;
      browserMsg = `${browser.name} ${browser.version}`;
    }
  }

  if (os) {
    const threshold = OS_VERSION_THRESHOLD[os.name];
    if (threshold && compareVersion(os.version, threshold) < 0) {
      isLowOS = true;
      osMsg = `${os.name} ${os.version}`;
    }
  }

  const isLow = isLowBrowser || isLowOS;

  let message = DEFAULT_MESSAGE;
  if (isLowBrowser && isLowOS) {
    message = `您的 ${browserMsg} 和 ${osMsg} 版本过低，可能会影响体验，建议升级。`;
  } else if (isLowBrowser) {
    message = `您的 ${browserMsg} 版本过低，可能会影响体验，建议升级到最新版本。`;
  } else if (isLowOS) {
    message = `您的 ${osMsg} 系统版本过低，可能会影响体验，建议升级。`;
  }

  return { isLow, message };
}

export interface BrowserSupportResult {
  /** 能否正常使用本站 */
  ok: boolean;
  /** 可直接展示给用户的提示文案（ok 时为空串） */
  message: string;
  /** 命中的问题项，便于排查 */
  problems: string[];
}

/** 建议升级到的浏览器版本说明 */
export const REQUIRED_BROWSER_HINT = "Chrome 111+ / Edge 111+ / Safari 16.4+ / Firefox 128+";

/**
 * 判定当前浏览器能否正常使用本站。
 *
 * 以**特性检测为准**（低版本浏览器缺的是 CSS 能力，而不只是一个版本号），
 * UA 仅用于把提示文案写得更具体。这样 360 / QQ / 夸克 / Arc 等套壳浏览器
 * 不会漏报，能跑起来的浏览器也不会被误报。
 */
export function checkBrowserSupport(): BrowserSupportResult {
  const css = supportsModernCss();
  const problems = [...css.missing];

  // Vue 3 的响应式依赖 Proxy，缺失则应用无法启动
  if (typeof Proxy === "undefined") problems.push("Proxy");

  if (problems.length === 0) {
    return { ok: true, message: "", problems: [] };
  }

  const browser = typeof navigator === "undefined" ? null : parseBrowser();
  const detected = browser ? `检测到 ${browser.name} ${browser.version}` : "当前浏览器";

  return {
    ok: false,
    message: `${detected}，本站需要 ${REQUIRED_BROWSER_HINT} 才能正常显示，请升级浏览器后重试。`,
    problems,
  };
}

export type BrowserEnvironment = "normal" | "wechat" | "problem";

export function detectEnvironment(): BrowserEnvironment {
  const ua = navigator.userAgent.toLowerCase();

  if (ua.includes("micromessenger")) {
    console.info("📱 检测到微信浏览器");
    return "wechat";
  }
  if (ua.includes("mqqbrowser") || ua.includes("baiduboxapp") || ua.includes("qq/")) {
    console.warn("⚠️ 检测到问题浏览器（QQ/百度）");
    return "problem";
  }
  console.info("🌐 检测到标准浏览器环境");
  return "normal";
}
